import React, { useEffect, useState } from 'react';
import { Modal, IconButton, Box, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import styled, { keyframes } from 'styled-components';

const popIn = keyframes`
	0%   { transform: scale(0.88) translateY(-18px); opacity: 0; }
	60%  { transform: scale(1.015) translateY(0);    opacity: 1; }
	100% { transform: scale(1) translateY(0);        opacity: 1; }
`;

const popOut = keyframes`
	0%   { transform: scale(1) translateY(0);        opacity: 1; }
	100% { transform: scale(0.92) translateY(-8px);  opacity: 0; }
`;

const haloPulse = keyframes`
	0%   { box-shadow: 0 0 0 0 currentColor; opacity: 0.55; }
	100% { box-shadow: 0 0 0 16px currentColor; opacity: 0; }
`;

const progressShrink = keyframes`
	from { transform: scaleX(1); }
	to   { transform: scaleX(0); }
`;

const Card = styled.div`
	position: relative;
	width: 100%;
	max-width: 440px;
	border-radius: 18px;
	overflow: hidden;
	outline: none;
	background: ${p => p.bg};
	color: ${p => p.fg};
	box-shadow: 0 22px 56px rgba(0, 0, 0, 0.32), 0 4px 14px rgba(0, 0, 0, 0.18);
	animation: ${p => (p.closing ? popOut : popIn)}
		${p => (p.closing ? '180ms' : '340ms')}
		cubic-bezier(0.22, 1, 0.36, 1) forwards;
`;

const AccentBar = styled.div`
	height: 4px;
	width: 100%;
	background: ${p => p.color};
`;

const Body = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 14px;
	padding: 20px 18px 18px 22px;
`;

const IconHalo = styled.div`
	position: relative;
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 46px;
	height: 46px;
	border-radius: 50%;
	background: ${p => p.bg};
	color: ${p => p.color};
	& svg { font-size: 28px; }
	&::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 50%;
		color: ${p => p.color};
		animation: ${haloPulse} 1.6s ease-out infinite;
	}
`;

const TextWrap = styled.div`
	flex: 1;
	min-width: 0;
	padding-top: 4px;
`;

const Title = styled.div`
	font-size: 1.55rem;
	font-weight: 600;
	line-height: 1.25;
	letter-spacing: 0.1px;
`;

const Message = styled.div`
	font-size: 1.3rem;
	line-height: 1.55;
	opacity: 0.82;
	margin-top: 4px;
	word-break: break-word;
	white-space: pre-line;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 0 18px 16px;
`;

const ProgressTrack = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 3px;
	background: ${p => (p.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
`;

const ProgressFill = styled.div`
	height: 100%;
	background: ${p => p.color};
	transform-origin: left center;
	animation: ${progressShrink} ${p => p.duration}ms linear forwards;
`;

const VARIANTS = {
	success: { color: '#22c55e', icon: CheckCircleIcon, defaultTitle: 'Success' },
	error:   { color: '#ef4444', icon: ErrorIcon,       defaultTitle: 'Something went wrong' },
	warning: { color: '#f59e0b', icon: WarningIcon,     defaultTitle: 'Heads up' },
	info:    { color: '#3b82f6', icon: InfoIcon,        defaultTitle: 'For your information' }
};

const useStyles = makeStyles(() => ({
	modal: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		padding: '64px 16px 16px',
		zIndex: 2000
	},
	close: {
		color: 'inherit',
		opacity: 0.65,
		marginTop: 2,
		'&:hover': { opacity: 1 }
	}
}));

function hexToRgba(hex, alpha) {
	const h = hex.replace('#', '');
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const AlertModalOmni = ({
	open,
	onClose,
	type = 'success',
	title,
	message = '',
	autoHideDuration = 3500,
	showCloseButton = true,
	actions,
	hideProgress = false,
	disableBackdropClick = false,
	disableEscapeKeyDown = false
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const variant = VARIANTS[type] || VARIANTS.info;
	const Icon = variant.icon;

	const isDark = theme.palette.type === 'dark';
	const cardBg = isDark ? '#1e2126' : '#ffffff';
	const cardFg = isDark ? '#f3f4f6' : '#111827';
	const haloBg = hexToRgba(variant.color, isDark ? 0.18 : 0.12);

	const [closing, setClosing] = useState(false);

	useEffect(() => {
		if (open) setClosing(false);
	}, [open]);

	useEffect(() => {
		if (!open || !autoHideDuration || autoHideDuration <= 0) return undefined;
		const t = setTimeout(() => triggerClose(), autoHideDuration);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, autoHideDuration]);

	const triggerClose = () => {
		setClosing(true);
		setTimeout(() => {
			setClosing(false);
			if (onClose) onClose();
		}, 180);
	};

	const handleBackdrop = (event, reason) => {
		if (disableBackdropClick && reason === 'backdropClick') return;
		if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
		triggerClose();
	};

	return (
		<Modal
			open={!!open}
			onClose={handleBackdrop}
			className={classes.modal}
			BackdropProps={{
				style: {
					backgroundColor: 'rgba(0, 0, 0, 0.45)',
					backdropFilter: 'blur(2px)'
				}
			}}
			closeAfterTransition
		>
			<Card bg={cardBg} fg={cardFg} closing={closing} role="alertdialog" aria-live="polite">
				<AccentBar color={variant.color} />
				<Body>
					<IconHalo bg={haloBg} color={variant.color}>
						<Icon />
					</IconHalo>
					<TextWrap>
						<Title>{title || variant.defaultTitle}</Title>
						{message && <Message>{message}</Message>}
					</TextWrap>
					{showCloseButton && (
						<IconButton size="small" className={classes.close} onClick={triggerClose} aria-label="close">
							<CloseIcon fontSize="small" />
						</IconButton>
					)}
				</Body>
				{actions && <Actions>{actions}</Actions>}
				{!hideProgress && autoHideDuration > 0 && (
					<ProgressTrack dark={isDark}>
						<ProgressFill
							key={`${open}-${autoHideDuration}-${type}-${message}`}
							color={variant.color}
							duration={autoHideDuration}
						/>
					</ProgressTrack>
				)}
			</Card>
		</Modal>
	);
};

export default AlertModalOmni;
